import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "PharmaceuticalFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Food & Pharmaceutical Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Mesh & Sintered Media for Pharmaceutical Filtration",
      lead: "Engineered diffusion-bonded sintered laminates, precision Dutch weaves, and sanitary filter cartridges designed for active pharmaceutical ingredient (API) processing, sterile venting, crystallization slurry recovery, and high-purity liquid polishing.",
      secondaryCta: {
        label: "Food & Pharmaceutical Filtration",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Precision sintered stainless steel multilayer filter element for pharmaceutical processing",
      },
      cardNote: "Diffusion-bonded metallic construction & non-shedding media",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Micron Range", value: "1 µm – 100 µm" },
        { label: "Alloy Metallurgy", value: "AISI 316L" },
        { label: "Media Construction", value: "Sintered Laminate / Dutch Weave" },
        { label: "Cleanability", value: "CIP / SIP Steam Cycles" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Non-Shedding Pore-Stable Separation for High-Purity Pharmaceutical Streams",
      paragraphs: [
        "Pharmaceutical synthesis circuits and active pharmaceutical ingredient (API) isolation units require separation media that preserve batch purity without shedding fibers or particulate contaminants into the effluent. Delicate crystallization cakes, solvent washes, and parenteral clarification fluids demand stable pore geometry under fluctuating differential pressures.",
        "MutualFilter supplies all-metal separation media including diffusion-bonded 5-layer sintered mesh plates, compacted plain and twilled Dutch weave filter cloth, and precision-welded cylindrical cartridges. Sintering under vacuum furnace conditions bonds wire contact points permanently, eliminating aperture deformation and preventing wire displacement during operation.",
        "Fabricated from passivated AISI 316L stainless steel, every screen and filter component is designed to withstand aggressive organic synthesis solvents, routine clean-in-place (CIP) washdowns, and cyclic steam-in-place (SIP) thermal regimes while maintaining mechanical integrity.",
      ],
      keyPoints: [
        "Diffusion-bonded sintered laminates and compacted Dutch weaves guarantee zero fiber release into critical drug streams",
        "Calibrated micro-apertures down to 1 µm ensure precise particulate retention for API isolation and parenteral polishing",
        "Passivated AISI 316L all-metal construction withstands synthesis solvents, CIP washdowns, and repeated SIP cycles",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Stainless steel sintered mesh media engineered for pharmaceutical separation equipment",
      },
    },
    specTable: {
      heading: "Filter Media Configurations for Pharmaceutical Processing",
      intro: "Standard metallic separation configurations tailored for Nutsche agitation filters, sterile gas vents, and solvent recovery loops.",
      columns: [
        { key: "unit", label: "Process Unit", align: "left" },
        { key: "media", label: "Media Construction", align: "left" },
        { key: "rating", label: "Nominal Rating", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "cleaning", label: "Cleaning & Sterilization", align: "right" },
      ],
      rows: [
        {
          unit: "Nutsche Filter Dryers (API Cake)",
          media: "5-layer sintered laminate disc / plate",
          rating: "5 µm – 50 µm",
          alloys: "AISI 316L",
          cleaning: "In-situ CIP wash & solvent flush",
        },
        {
          unit: "Sterile Gas & Vent Housings",
          media: "Pleated / cylindrical sintered mesh cartridge",
          rating: "1 µm – 10 µm",
          alloys: "AISI 316L",
          cleaning: "SIP steam & hot air regeneration",
        },
        {
          unit: "Parenteral Polishing & Clarification",
          media: "Twilled Dutch weave wire cloth",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Ultrasonic bath & forward flush",
        },
        {
          unit: "Crystallization Mother Liquor Recovery",
          media: "Plain Dutch weave / sintered cylinder",
          rating: "10 µm – 100 µm",
          alloys: "AISI 316L",
          cleaning: "Automated solvent backwash",
        },
      ],
      caption: "All aperture ratings, disc diameters, and cartridge fittings are manufactured to order according to process engineering specifications.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our stainless steel pharmaceutical filter media combine fixed pore geometry, smooth surface finishes, and metallurgical durability across repetitive operational cycles.",
      items: [
        {
          title: "Permanent Diffusion Bonding",
          text: "Vacuum furnace thermal sintering fuses every wire intersection into a monolithic plate, preventing media deformation or particulate displacement under cyclic flow reversals.",
        },
        {
          title: "Non-Shedding All-Metal Media",
          text: "Unlike polymeric or fiber-based media, stainless steel woven and sintered structures eliminate fiber migration and media shedding into high-purity fluid streams.",
        },
        {
          title: "Smooth Clean-In-Place Surfaces",
          text: "Passivated metallic surfaces reduce product retention, facilitate rapid cake discharge in Nutsche dryers, and support thorough CIP and SIP decontamination cycles.",
        },
        {
          title: "Solvent & Chemical Compatibility",
          text: "High-grade AISI 316L stainless steel provides broad resistance against organic solvents, synthesis reagents, and acidic or alkaline clean-down chemistries.",
        },
      ],
    },
    related: {
      heading: "Related Applications & Recommended Products",
      intro: "Explore complementary food and pharmaceutical processing applications alongside precision metallic filter media manufactured for sanitary equipment.",
      cards: [
        {
          title: "Food & Pharmaceutical Filtration",
          text: "Comprehensive metallic filtration media engineered for sanitary processing, beverage clarification, and pharmaceutical purification.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Polished stainless steel sanitary filtration equipment for food and pharmaceutical applications",
          },
        },
        {
          title: "Food Processing Filtration",
          text: "Sanitary woven wire mesh, wedge wire screens, and strainer elements for dairy, edible oil, starch, and beverage clarifying lines.",
          to: "/FoodProcessingFiltrationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Food Processing",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
            alt: "Sanitary stainless steel wire mesh filter components in food processing facility",
          },
        },
        {
          title: "Sintered Wire Mesh",
          text: "Diffusion-bonded 5-layer and multi-layer sintered laminates engineered for rigid Nutsche filter plates and fluidizing beds.",
          to: "/SinteredWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Sintered Mesh",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Multilayer sintered wire mesh laminate disc for pharmaceutical Nutsche filter dryers",
          },
        },
        {
          title: "Filter Components",
          text: "Custom fabricated stainless steel filter cartridges, cylindrical elements, and sanitary baskets for high-purity filtration housings.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "All-metal sanitary filter cartridge for pharmaceutical steam and liquid filtration",
          },
        },
      ],
    },
    cta: {
      id: "pharma-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Pharmaceutical Filter Media Specification",
      lead: "Provide your required micron rating, element dimensions, housing connection type, and alloy specifications for a dedicated engineering review and quotation.",
      secondaryLabel: "Food & Pharmaceutical Filtration",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "PharmaceuticalFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "食品与制药过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "制药工艺精密不锈钢丝网与烧结过滤介质",
      lead: "专为原料药（API）提纯结晶、无菌排气呼吸器、母液过滤洗涤干燥（三合一）及高纯流体精细澄清定制的扩散焊接多层烧结网板、密纹编织滤布与卫生级金属滤芯。",
      secondaryCta: {
        label: "食品与制药过滤总览",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "用于制药工艺的高精度多层不锈钢烧结滤芯结构",
      },
      cardNote: "全金属扩散烧结结构与零纤维脱落介质",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "过滤精度范围", value: "1 µm – 100 µm" },
        { label: "常用材质牌号", value: "AISI 316L" },
        { label: "过滤介质构型", value: "烧结复合网 / 密纹编织网" },
        { label: "清洗灭菌方式", value: "CIP / SIP 在线蒸汽" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "针对高纯制药工艺流体的高刚性无介质脱落固液分离",
      paragraphs: [
        "化学药物合成与原料药（API）分离纯化对过滤介质有着严苛的洁净度要求，介质本身绝不允许向药液流中脱落纤维或颗粒杂质。精细的结晶物料滤饼洗涤、母液回收以及注射液前端澄清，要求过滤微孔在脉动压差下保持恒定几何形状。",
        "MutualFilter 提供全金属精密过滤介质，包括真空扩散焊接 5 层不锈钢烧结复合网板、高密实平纹及斜纹荷兰编织丝网，以及精工焊接圆柱滤芯。通过高温真空烧结工艺将丝网各交织点牢固粘结，彻底消除孔隙变形与细丝位移风险。",
        "产品选用钝化处理的 AISI 316L 不锈钢精工制造，稳定耐受有机合成溶剂腐蚀、高频 CIP 在位化学清洗及多次 SIP 在位蒸汽灭菌，长期保持出色的机械刚性与微米级分离精度。",
      ],
      keyPoints: [
        "真空扩散粘结烧结复合网与密实荷兰编织网杜绝任何纤维与颗粒脱落进入药液流",
        "经过精确校准的微米级微孔结构（低至 1 µm）确保原料药母液拦截与精细澄清效果",
        "酸洗钝化 AISI 316L 全金属结构耐受各类有机溶剂、CIP 在位清洗及多次 SIP 蒸汽灭菌",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "用于制药分离设备的不锈钢烧结网介质",
      },
    },
    specTable: {
      heading: "制药工艺常用金属过滤元件配置参数",
      intro: "以下列出适用于过滤洗涤干燥机（三合一）、无菌呼吸排气及母液澄清工况的典型配置。",
      columns: [
        { key: "unit", label: "应用设备 / 工艺环节", align: "left" },
        { key: "media", label: "核心构型与介质", align: "left" },
        { key: "rating", label: "名义过滤精度", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "cleaning", label: "清洗灭菌方式", align: "right" },
      ],
      rows: [
        {
          unit: "三合一过滤洗涤干燥机（API滤饼）",
          media: "5层扩散焊接烧结复合网板",
          rating: "5 µm – 50 µm",
          alloys: "AISI 316L",
          cleaning: "机内 CIP 喷淋冲洗与溶剂浸洗",
        },
        {
          unit: "储罐无菌排气呼吸器",
          media: "折波 / 圆筒不锈钢烧结滤芯",
          rating: "1 µm – 10 µm",
          alloys: "AISI 316L",
          cleaning: "SIP 蒸汽灭菌与热风干燥",
        },
        {
          unit: "注射剂母液前级精制澄清",
          media: "斜纹荷兰编织密纹丝网",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "超声波清洗与正向溶剂冲洗",
        },
        {
          unit: "结晶母液回收与颗粒捕集",
          media: "平纹荷兰编织网 / 烧结滤筒",
          rating: "10 µm – 100 µm",
          alloys: "AISI 316L",
          cleaning: "溶剂脉冲自动化反洗",
        },
      ],
      caption: "各项截留精度、网板直径及接口安装形式均可按实际工程图纸与工艺要求定制加工。",
    },
    features: {
      heading: "关键运行与结构技术优势",
      intro: "我们的不锈钢制药过滤介质兼具微孔几何稳定性、光洁卫生表面及出色的耐腐蚀冶金耐久性。",
      items: [
        {
          title: "高温真空扩散焊接",
          text: "真空炉高温烧结使所有金属丝交织点形成分子级冶金结合，一体成型，在脉冲压差与反向流体冲刷下永不松动移位。",
        },
        {
          title: "零脱落全金属介质",
          text: "与高分子聚合物或纺织滤袋相比，纯金属编织与烧结板材杜绝介质破损降解，从根源消除微粒进入高纯工艺流的隐患。",
        },
        {
          title: "光洁平滑便于在位清洁",
          text: "钝化精研金属表面大幅降低晶体挂壁残留，在干燥机内便于机械刮刀彻底脱饼，并能充分响应 CIP 与 SIP 清洗循环。",
        },
        {
          title: "广泛溶剂化学耐受性",
          text: "高纯度 AISI 316L 不锈钢对酸性、碱性清洗介质以及丙酮、甲醇、二氯甲烷等各类复杂有机合成溶剂具备可靠耐受能力。",
        },
      ],
    },
    related: {
      heading: "相关应用方案与推荐产品",
      intro: "浏览 MutualFilter 为食品与医药工业打造的卫生级应用方案及精密金属过滤产品。",
      cards: [
        {
          title: "食品与制药过滤",
          text: "涵盖饮料澄清、物料浓缩、高纯流体除杂及药液分级过滤的全金属分离介质综合方案。",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "用于食品与制药行业的抛光不锈钢卫生级过滤装置",
          },
        },
        {
          title: "食品加工过滤",
          text: "适用于乳品除杂、食用油压榨脱蜡、糖浆澄清与饮料灌装前级保护的卫生级金属滤材与筛网。",
          to: "/FoodProcessingFiltrationPage",
          badge: "同级应用",
          linkLabel: "探索食品加工过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
            alt: "食品加工车间内使用的卫生级不锈钢丝网过滤元件",
          },
        },
        {
          title: "烧结丝网",
          text: "高强度 5 层及多层扩散焊接金属烧结复合板，专为三合一过滤底板及流化床透气板设计。",
          to: "/SinteredWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索烧结丝网",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "用于制药过滤洗涤干燥机的多层烧结不锈钢网板",
          },
        },
        {
          title: "过滤组件",
          text: "定制焊接金属圆筒滤芯、折波滤筒及卫生级滤篮组件，匹配各类卫生级外壳与管道连接。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "用于制药蒸汽与卫生液体过滤的全金属滤芯",
          },
        },
      ],
    },
    cta: {
      id: "pharma-quote",
      eyebrow: "定制方案咨询",
      heading: "获取制药过滤网板与金属滤芯技术报价",
      lead: "请提供您的目标过滤精度、滤板/滤芯尺寸规格、法兰安装接口及材质牌号要求，我们的工程团队将为您提供针对性的技术选型与报价方案。",
      secondaryLabel: "返回食品与制药过滤",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "PharmaceuticalFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Filtración de Alimentos y Farmacia",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas y Medios Sinterizados de Acero Inoxidable para Filtración Farmacéutica",
      lead: "Laminados sinterizados unidos por difusión, tejidos Dutch de alta precisión y cartuchos filtrantes sanitarios diseñados para el aislamiento de principios activos (API), venteos estériles, secadores Nutsche y clarificación de fluidos de alta pureza.",
      secondaryCta: {
        label: "Filtración de Alimentos y Farmacia",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Elemento filtrante multicapa de acero inoxidable sinterizado de precisión para procesos farmacéuticos",
      },
      cardNote: "Estructura metálica unida por difusión y medios sin desprendimiento",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango Micrométrico", value: "1 µm – 100 µm" },
        { label: "Grados de Aleación", value: "AISI 316L" },
        { label: "Construcción", value: "Laminado Sinterizado / Tejido Dutch" },
        { label: "Limpieza", value: "Ciclos de Vapor CIP / SIP" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Separación Estable y Sin Desprendimiento para Fluidos Farmacéuticos de Alta Pureza",
      paragraphs: [
        "Las instalaciones de síntesis farmacéutica y purificación de principios activos (API) requieren medios filtrantes que preserven la pureza del lote sin desprender fibras ni partículas contaminantes en el producto filtrado. El lavado de tortas cristalinas y el pulido de fluidos parenterales exigen una geometría de poro inalterable bajo presiones diferenciales variables.",
        "MutualFilter suministra medios de separación totalmente metálicos que incluyen placas de malla sinterizada de 5 capas unidas por difusión, telas Dutch lisas y cruzadas compactas y cartuchos cilíndricos soldados de precisión. El sinterizado al vacío une permanentemente los alambres en cada cruce, impidiendo desplazamientos de apertura.",
        "Fabricados en acero inoxidable AISI 316L pasivado, los componentes resisten solventes orgánicos corrosivos, lavajes CIP alcalinos y esterilizaciones térmicas continuas por vapor SIP, conservando rigidez mecánica e integridad dimensional.",
      ],
      keyPoints: [
        "Los laminados sinterizados por difusión y tejidos Dutch densos eliminan el riesgo de desprendimiento de fibras en fluidos críticos",
        "Microaberturas calibradas hasta 1 µm aseguran una retención exacta para aislamiento de API y clarificación parenteral",
        "La construcción totalmente metálica en AISI 316L pasivado soporta solventes de síntesis, lavados CIP y ciclos SIP repetidos",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Medios de malla sinterizada de acero inoxidable para equipos de separación farmacéutica",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios Filtrantes para Procesos Farmacéuticos",
      intro: "Medios metálicos representativos para secadores Nutsche, venteos estériles y circuitos de recuperación de solventes.",
      columns: [
        { key: "unit", label: "Unidad de Proceso", align: "left" },
        { key: "media", label: "Construcción del Medio", align: "left" },
        { key: "rating", label: "Retención Nominal", align: "right" },
        { key: "alloys", label: "Aleaciones Típicas", align: "right" },
        { key: "cleaning", label: "Limpieza y Esterilización", align: "right" },
      ],
      rows: [
        {
          unit: "Filtros Secadores Nutsche (Torta API)",
          media: "Placa / disco laminado sinterizado de 5 capas",
          rating: "5 µm – 50 µm",
          alloys: "AISI 316L",
          cleaning: "Lavado CIP in situ y lavado con solvente",
        },
        {
          unit: "Venteos y Respiradores Estériles",
          media: "Cartucho plisado / cilíndrico sinterizado",
          rating: "1 µm – 10 µm",
          alloys: "AISI 316L",
          cleaning: "Vapor SIP y regeneración por aire caliente",
        },
        {
          unit: "Clarificación y Pulido Parenteral",
          media: "Malla de alambre en tejido Dutch cruzado",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Baño ultrasónico y flujo directo",
        },
        {
          unit: "Recuperación de Aguas Madres de Cristalización",
          media: "Tejido Dutch liso / cilindro sinterizado",
          rating: "10 µm – 100 µm",
          alloys: "AISI 316L",
          cleaning: "Retrolavado automatizado con solvente",
        },
      ],
      caption: "Todas las clasificaciones de retención, diámetros de disco y conexiones se fabrican bajo plano según especificaciones de proceso.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios filtrantes para farmacia combinan estabilidad de poro, acabado sanitario liso y alta durabilidad metalúrgica.",
      items: [
        {
          title: "Unión por Difusión Permanente",
          text: "El sinterizado térmico en horno de vacío fusiona los puntos de contacto del alambre, evitando deformaciones de poro bajo cambios de flujo.",
        },
        {
          title: "Medio Sin Desprendimiento Totalmente Metálico",
          text: "A diferencia de filtros poliméricos, las estructuras metálicas soldadas y sinterizadas eliminan cualquier migración de fibras hacia el producto final.",
        },
        {
          title: "Superficies Lisas Aptas para CIP",
          text: "El acabado pasivado minimiza adherencias de producto, facilita la descarga de tortas en filtros Nutsche y asegura limpiezas CIP/SIP eficaces.",
        },
        {
          title: "Compatibilidad con Solventes y Químicos",
          text: "El acero AISI 316L ofrece resistencia integral frente a solventes orgánicos complejos y reactivos de limpieza ácidos o alcalinos.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Productos Relacionados",
      intro: "Conozca soluciones complementarias de filtración sanitaria y medios metálicos de precisión fabricados por MutualFilter.",
      cards: [
        {
          title: "Filtración de Alimentos y Farmacia",
          text: "Medios metálicos diseñados para procesamiento sanitario, clarificación de bebidas y purificación farmacéutica.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Equipo de filtración sanitario en acero inoxidable pulido para alimentos y farmacia",
          },
        },
        {
          title: "Filtración en Procesamiento de Alimentos",
          text: "Mallas tejidas y elementos de alambre en cuña para clarificación de jarabes, lácteos, aceites y bebidas.",
          to: "/FoodProcessingFiltrationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Procesamiento de Alimentos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
            alt: "Componentes filtrantes de malla de acero inoxidable en planta alimentaria",
          },
        },
        {
          title: "Malla Sinterizada",
          text: "Laminados multicapa y de 5 capas unidos por difusión para placas de filtros secadores Nutsche y lechos fluidizados.",
          to: "/SinteredWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Malla Sinterizada",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado de malla sinterizada multicapa para secadores de filtro Nutsche farmacéuticos",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cartuchos metálicos cilíndricos, tubos soldados y cestas sanitarias para carcasas de proceso de alta pureza.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Cartucho filtrante sanitario metálico para vapor y líquidos farmacéuticos",
          },
        },
      ],
    },
    cta: {
      id: "pharma-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Medios Filtrantes Farmacéuticos",
      lead: "Indique la retención requerida en micras, dimensiones del elemento, tipo de conexión y especificaciones de aleación para una propuesta técnica y económica.",
      secondaryLabel: "Volver a Alimentos y Farmacia",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "PharmaceuticalFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Lebensmittel- & Pharmafiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahl-Drahtgewebe & Sintermedien für die Pharmafiltration",
      lead: "Diffusionsgebundene Sinterlaminate, Präzisions-Tressengewebe und sanitäre Filterkerzen für die Wirkstoffherstellung (API), sterile Be- und Entlüftung, Nutschenfiltertrockner und Reinstflüssigkeits-Politur.",
      secondaryCta: {
        label: "Lebensmittel- & Pharmafiltration",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Präzisions-Edelstahl-Mehrlagenfilterelement für pharmazeutische Prozesse",
      },
      cardNote: "Diffusionsgebundener Ganzmetall-Aufbau & partikelfreie Medien",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Porenweitenbereich", value: "1 µm – 100 µm" },
        { label: "Werkstoffe", value: "AISI 316L" },
        { label: "Konstruktion", value: "Sinterlaminat / Tressengewebe" },
        { label: "Reinigbarkeit", value: "CIP / SIP Dampfzyklen" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Partikelfreie, porenstabile Fest-Flüssig-Trennung für Reinstmedien der Pharmazie",
      paragraphs: [
        "Pharmazeutische Synthesestufen und Anlagen zur Isolierung pharmazeutischer Wirkstoffe (APIs) stellen höchste Anforderungen an die Reinheit von Trennmedien: Es dürfen keinerlei Fasern oder Partikel in das Filtrat abgegeben werden. Das Waschen empfindlicher Kristallkuchen und das Polieren parenteraler Lösungen verlangen eine konstante Porengeometrie unter wechselnden Differenzdrücken.",
        "MutualFilter liefert vollmetallische Trennmedien wie diffusionsgebundene 5-Lagen-Sinterverbundplatten, dichte glatte und köpergewebte Tressen sowie präzisionsgeschweißte Zylinderkerzen. Die Hochvakuum-Sinterung verbindet die Drahtkreuzungspunkte molekular und verhindert Porenverzerrungen oder Drahtverschiebungen im laufenden Betrieb.",
        "Gefertigt aus passiviertem Edelstahl AISI 316L widerstehen alle Filterkomponenten korrosiven Syntheselösungsmitteln, aggressiven CIP-Reinigungsmedien und wiederholten SIP-Dampfsterilisationen bei vollständiger Beibehaltung mechanischer Stabilität.",
      ],
      keyPoints: [
        "Diffusionsgebundene Sinterlaminate und dichte Tressengewebe verhindern jeglichen Faserabrieb in sensible Wirkstoffströme",
        "Präzise kalibrierte Mikroporen bis hinab zu 1 µm gewährleisten exakte Feststoffabtrennung bei der API-Isolierung",
        "Passivierter Edelstahl AISI 316L bietet hohe Beständigkeit gegen Syntheselösemittel, CIP-Spülungen und SIP-Dampfzyklen",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Edelstahl-Sintergewebemedien für pharmazeutische Trennapparate",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für pharmazeutische Prozesse",
      intro: "Repräsentative metallische Filtermedien für Nutschenfiltertrockner, Sterilbelüftungen und Lösemittelrückgewinnung.",
      columns: [
        { key: "unit", label: "Prozesseinheit", align: "left" },
        { key: "media", label: "Filtermedium-Aufbau", align: "left" },
        { key: "rating", label: "Nennfilterfeinheit", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "cleaning", label: "Reinigung & Sterilisation", align: "right" },
      ],
      rows: [
        {
          unit: "Nutschenfiltertrockner (API-Filterkuchen)",
          media: "5-Lagen diffusionsgebundene Sinterplatte",
          rating: "5 µm – 50 µm",
          alloys: "AISI 316L",
          cleaning: "In-situ CIP-Reinigung & Lösemittelspülung",
        },
        {
          unit: "Sterilgas- & Belüftungsgehäuse",
          media: "Plissierte / zylindrische Sinterkerze",
          rating: "1 µm – 10 µm",
          alloys: "AISI 316L",
          cleaning: "SIP-Dampf & Heißluftregeneration",
        },
        {
          unit: "Parenterale Politur & Feinabtrennung",
          media: "Köpertressengewebe aus Edelstahldraht",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Ultraschallbad & Vorwärtsspülung",
        },
        {
          unit: "Mutterlaugen-Rückgewinnung",
          media: "Glatte Tresse / Sinterzylinder",
          rating: "10 µm – 100 µm",
          alloys: "AISI 316L",
          cleaning: "Automatisierte Lösemittel-Rückspülung",
        },
      ],
      caption: "Alle Filterfeinheiten, Plattendurchmesser und Anschlüsse werden kundenspezifisch nach Konstruktionsvorgaben gefertigt.",
    },
    features: {
      heading: "Betriebliche & Fertigungstechnische Vorzüge",
      intro: "Unsere pharmazeutischen Filtermedien verbinden formstabile Porengeometrie, glatte Sanitärflächen und dauerhafte metallurgische Beständigkeit.",
      items: [
        {
          title: "Dauerhafte Diffusionsverbindung",
          text: "Die Vakuum-Diffusionssinterung verschmilzt alle Drahtkreuzungspunkte zu einer monolithischen Einheit, die Druckstößen und Fließumkehrungen standhält.",
        },
        {
          title: "Partikelfreie Vollmetallmedien",
          text: "Im Gegensatz zu Kunststoffgeweben verhindern vollmetallische Filterplatten und -kerzen jegliches Lösen von Faserpartikeln in reine Produktströme.",
        },
        {
          title: "Glatte, CIP-optimierte Oberflächen",
          text: "Passivierte Oberflächen minimieren Produktanhaftungen, erleichtern den Kuchenaustrag in Nutschentrocknern und gewährleisten sichere CIP/SIP-Abläufe.",
        },
        {
          title: "Lösemittel- & Chemikalienresistenz",
          text: "Der Werkstoff AISI 316L widersteht aggressiven organischen Lösemitteln, Synthesereagenzien sowie sauren und alkalischen Reinigungsmitteln.",
        },
      ],
    },
    related: {
      heading: "Verwandte Anwendungen & Empfohlene Produkte",
      intro: "Entdecken Sie weitere lebensmittel- und pharmagerechte Trennlösungen und präzise Metallfiltermedien aus der Fertigung von MutualFilter.",
      cards: [
        {
          title: "Lebensmittel- & Pharmafiltration",
          text: "Ganzheitliche metallische Trennmedien für hygienische Verarbeitung, Getränkeklärung und pharmazeutische Aufreinigung.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Polierte Edelstahl-Sanitärfiltrationsanlage für Lebensmittel und Pharmazie",
          },
        },
        {
          title: "Filtration in der Lebensmittelverarbeitung",
          text: "Sanitäre Drahtgewebe und Spaltsiebe für Molkereiprodukte, Speiseöle, Stärke und Getränkeklärung.",
          to: "/FoodProcessingFiltrationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Lebensmittelverarbeitung entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
            alt: "Sanitäre Edelstahlgewebe-Filterelemente in Lebensmittelbetrieb",
          },
        },
        {
          title: "Sintergewebe",
          text: "Diffusionsgebundene 5-Lagen- und Mehrlagen-Sinterplatten für Nutschenfilterböden und Wirbelschichtapparate.",
          to: "/SinteredWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Sintergewebe entdecken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Mehrlagige Sinterscheibe für pharmazeutische Nutschenfiltertrockner",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Geschweißte Filterkerzen, zylindrische Elemente und Siebkörbe für anspruchsvolle Reinstgehäuse.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Filterkomponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Ganzmetall-Sanitärfilterkerze für pharmazeutischen Dampf und Flüssigkeiten",
          },
        },
      ],
    },
    cta: {
      id: "pharma-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für pharmazeutische Filtermedien anfordern",
      lead: "Nennen Sie Filterfeinheit, Abmessungen, Gehäuseanschluss und Werkstoffspezifikation für eine fundierte technische Prüfung und Angebotserstellung.",
      secondaryLabel: "Zurück zu Lebensmittel & Pharma",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "PharmaceuticalFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Filtration Agroalimentaire et Pharmaceutique",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toiles Métalliques et Médias Frittés pour la Filtration Pharmaceutique",
      lead: "Stratifiés frittés par diffusion, toiles tissées hollandaises de haute précision et cartouches filtrantes sanitaires pour la synthèse de principes actifs (API), les évents stériles, les filtres Nutsche et la clarification de fluides purs.",
      secondaryCta: {
        label: "Filtration Agroalimentaire et Pharmaceutique",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Élément filtrant multicouche en acier inoxydable fritté de précision pour procédés pharmaceutiques",
      },
      cardNote: "Structure métallique unie par diffusion et média sans relargage",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage Micrométrique", value: "1 µm – 100 µm" },
        { label: "Nuances d'Alliages", value: "AISI 316L" },
        { label: "Fabrication", value: "Laminé Fritté / Tissage Hollandais" },
        { label: "Nettoyabilité", value: "Cycles Vapeur NEP / SEP" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Séparation Stable et Sans Relargage pour Fluides Pharmaceutiques de Haute Pureté",
      paragraphs: [
        "Les réacteurs de synthèse et ateliers d'isolement de principes actifs (API) exigent des médias filtrants préservant la pureté du lot sans libérer de fibres ni de débris métalliques dans le filtrat. L'essorage des gâteaux de cristallisation et le polissage des fluides injectables requièrent une géométrie de pores inaltérable sous pression différentielle variable.",
        "MutualFilter fournit des médias de séparation tout métal comprenant des disques frittés 5 couches soudés par diffusion, des toiles à tissage hollandais uni et sergé compactes, ainsi que des cartouches cylindriques soudées avec précision. Le frittage sous vide lie définitivement les fils aux points de croisement, éliminant tout risque de déformation d'ouverture.",
        "Fabriqués en acier inoxydable passivé AISI 316L, ces éléments résistent aux solvants de synthèse agressifs, aux lavages alcalins NEP et aux stérilisations répétées à la vapeur SEP, conservant une rigidité mécanique durable.",
      ],
      keyPoints: [
        "Les stratifiés frittés par diffusion et toiles hollandaises denses éliminent tout risque de relargage particulaire dans les flux de principes actifs",
        "Micro-ouvertures étalonnées jusqu'à 1 µm garantissant une rétention précise pour l'isolement d'API et la clarification",
        "Conception tout métal en AISI 316L passivé résistant aux solvants organiques, aux lavages NEP et aux cycles SEP",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Médias en toile frittée en acier inoxydable pour équipements de séparation pharmaceutique",
      },
    },
    specTable: {
      heading: "Configurations de Médias Filtrants pour Procédés Pharmaceutiques",
      intro: "Médias métalliques représentatifs pour sécheurs Nutsche, évents stériles et circuits de récupération de solvants.",
      columns: [
        { key: "unit", label: "Unité de Procédé", align: "left" },
        { key: "media", label: "Type de Média Filtrant", align: "left" },
        { key: "rating", label: "Seuil Nominal", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "cleaning", label: "Nettoyage & Stérilisation", align: "right" },
      ],
      rows: [
        {
          unit: "Sécheurs-Filtres Nutsche (Gâteau API)",
          media: "Plaque / disque fritté 5 couches soudé",
          rating: "5 µm – 50 µm",
          alloys: "AISI 316L",
          cleaning: "Lavage NEP in situ & rinçage solvant",
        },
        {
          unit: "Évents & Respirateurs Stériles",
          media: "Cartouche frittée plissée / cylindrique",
          rating: "1 µm – 10 µm",
          alloys: "AISI 316L",
          cleaning: "Vapeur SEP & régénération air chaud",
        },
        {
          unit: "Clarification & Polissage Injectables",
          media: "Toile tissée à tissage hollandais sergé",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Bain ultrasons & rinçage direct",
        },
        {
          unit: "Récupération d'Eaux Mères de Cristallisation",
          media: "Tissage hollandais uni / cylindre fritté",
          rating: "10 µm – 100 µm",
          alloys: "AISI 316L",
          cleaning: "Rétrolavage automatisé aux solvants",
        },
      ],
      caption: "Dimensions, seuils de coupure et raccords réalisés sur mesure conformément aux spécifications des plans d'ingénierie.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos toiles et médias frittés pharmaceutiques associent stabilité d'ouverture, état de surface sanitaire et durabilité métallurgique.",
      items: [
        {
          title: "Liaison Permanente par Diffusion",
          text: "Le frittage thermique sous vide fusionne chaque point de contact des fils en une plaque monolithique insensible aux inversions de flux.",
        },
        {
          title: "Média Tout Métal Sans Relargage",
          text: "À la différence des polymères, les toiles et plaques métalliques frittées éliminent tout détachement de fibres dans les flux de haute pureté.",
        },
        {
          title: "Surfaces Lisses Compatibles NEP",
          text: "L'état de surface passivé minimise l'adhérence des cristaux, accélère la décharge du gâteau dans les filtres Nutsche et optimise les cycles NEP/SEP.",
        },
        {
          title: "Compatibilité avec Solvants et Réactifs",
          text: "L'acier inoxydable AISI 316L résiste aux solvants organiques complexes, aux réactifs de synthèse et aux solutions de nettoyage caustiques.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez les solutions complémentaires de filtration sanitaire et les médias métalliques de précision conçus par MutualFilter.",
      cards: [
        {
          title: "Filtration Agroalimentaire et Pharmaceutique",
          text: "Solutions de filtration métallique pour procédés hygiéniques, clarification de boissons et purification pharmaceutique.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Équipement sanitaire de filtration en inox poli pour agroalimentaire et pharmacie",
          },
        },
        {
          title: "Filtration en Agroalimentaire",
          text: "Toiles métalliques et grilles à fil profilé pour la clarification de sirops, produits laitiers, huiles et boissons.",
          to: "/FoodProcessingFiltrationPage",
          badge: "Application Associée",
          linkLabel: "Explorer l'Agroalimentaire",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
            alt: "Éléments filtrants en toile d'acier inoxydable dans une usine agroalimentaire",
          },
        },
        {
          title: "Toile Frittée",
          text: "Laminés 5 couches et multicouches soudés par diffusion pour plateaux de filtres Nutsche et lits fluidisés.",
          to: "/SinteredWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer la Toile Frittée",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disque laminé fritté multicouche pour sécheurs-filtres Nutsche pharmaceutiques",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Cartouches cylindriques soudées, tubes métalliques et paniers sanitaires pour carters de process à haute pureté.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Cartouche filtrante sanitaire tout métal pour vapeur et fluides pharmaceutiques",
          },
        },
      ],
    },
    cta: {
      id: "pharma-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Médias Filtrants Pharmaceutiques",
      lead: "Précisez votre seuil de rétention, les cotes de l'élément, le type de raccordement et la nuance d'acier pour une étude technique et commerciale.",
      secondaryLabel: "Retour à l'Agroalimentaire & Pharmacie",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "PharmaceuticalFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Filtração de Alimentos e Farmacêutica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Telas de Aço Inoxidável e Meios Sinterizados para Filtração Farmacêutica",
      lead: "Laminados sinterizados por difusão, telas holandesas de precisão e cartuchos filtrantes sanitários projetados para recuperação de princípios ativos (API), respiros estéreis, secadores Nutsche e clarificação de fluidos de alta pureza.",
      secondaryCta: {
        label: "Filtração de Alimentos e Farmacêutica",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Elemento filtrante multicamada em aço inoxidável sinterizado de precisão para processos farmacêuticos",
      },
      cardNote: "Estrutura metálica unida por difusão e meios sem desprendimento",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa Micrométrica", value: "1 µm – 100 µm" },
        { label: "Ligas Metálicas", value: "AISI 316L" },
        { label: "Construção", value: "Laminado Sinterizado / Trama Holandesa" },
        { label: "Limpabilidade", value: "Ciclos de Vapor CIP / SIP" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Separação Estável e Sem Desprendimento para Fluidos Farmacêuticos de Alta Pureza",
      paragraphs: [
        "Unidades de síntese química e purificação de princípios ativos (API) requerem meios de separação que preservem rigorosamente a integridade dos lotes, sem liberar fibras ou partículas no filtrado. A lavagem de tortas cristalinas e o polimento de soluções parenterais exigem abertura de poro imutável sob pressões operacionais variáveis.",
        "A MutualFilter fabrica meios de separação totalmente metálicos, incluindo discos laminados sinterizados de 5 camadas unidos por difusão, telas holandesas lisas e sarja densas e cartuchos cilíndricos soldados com precisão. O processo de sinterização sob vácuo funde os pontos de contato dos fios, impedindo deformações de poro.",
        "Fabricados em aço inoxidável AISI 316L passivado, os elementos toleram solventes de síntese agressivos, ciclos cáusticos de lavagem CIP e esterilizações cíclicas a vapor SIP, mantendo estabilidade dimensional e mecânica contínua.",
      ],
      keyPoints: [
        "Laminados sinterizados por difusão e telas holandesas densas eliminam qualquer risco de desprendimento de fibras no produto final",
        "Microporos calibrados a partir de 1 µm asseguram retenção exata de partículas na cristalização e polimento parenteral",
        "Construção metálica em aço AISI 316L passivado resistente a solventes de síntese, limpezas CIP e ciclos frequentes de vapor SIP",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
        alt: "Meios de malha sinterizada em aço inoxidável para equipamentos de separação farmacêutica",
      },
    },
    specTable: {
      heading: "Configurações de Meios Filtrantes para Processos Farmacêuticos",
      intro: "Meios metálicos típicos para secadores-filtros Nutsche, respiradouros estéreis e recuperação de solventes.",
      columns: [
        { key: "unit", label: "Unidade de Processo", align: "left" },
        { key: "media", label: "Estrutura do Meio Filtrante", align: "left" },
        { key: "rating", label: "Retenção Nominal", align: "right" },
        { key: "alloys", label: "Ligas Típicas", align: "right" },
        { key: "cleaning", label: "Limpeza e Esterilização", align: "right" },
      ],
      rows: [
        {
          unit: "Secadores-Filtros Nutsche (Torta API)",
          media: "Placa / disco sinterizado de 5 camadas",
          rating: "5 µm – 50 µm",
          alloys: "AISI 316L",
          cleaning: "Lavagem CIP in situ & enxágue com solvente",
        },
        {
          unit: "Respiros e Ventilação Estéril",
          media: "Cartucho plissado / cilíndrico sinterizado",
          rating: "1 µm – 10 µm",
          alloys: "AISI 316L",
          cleaning: "Vapor SIP & regeneração com ar quente",
        },
        {
          unit: "Polimento e Clarificação Parenteral",
          media: "Tela tecida em trama holandesa sarja",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Banho ultrassônico & fluxo direto",
        },
        {
          unit: "Recuperação de Licor-Mãe de Cristalização",
          media: "Trama holandesa lisa / cilindro sinterizado",
          rating: "10 µm – 100 µm",
          alloys: "AISI 316L",
          cleaning: "Retrolavagem automatizada com solvente",
        },
      ],
      caption: "Todas as retenções micrométricas, diâmetros de placa e conexões são customizados sob encomenda conforme o projeto do cliente.",
    },
    features: {
      heading: "Destaques Construtivos e Operacionais",
      intro: "Nossos meios filtrantes farmacêuticos reúnem retenção estável de poros, acabamento sanitário liso e durabilidade metalúrgica superior.",
      items: [
        {
          title: "União Permanente por Difusão",
          text: "A sinterização térmica em forno de alto vácuo une os cruzamentos de fios em uma matriz rígida imune a variações e inversões de fluxo.",
        },
        {
          title: "Meios Totalmente Metálicos Sem Desprendimento",
          text: "Diferente de meios poliméricos, elementos em telas metálicas soldadas e placas sinterizadas não sofrem desfibramento para o fluxo de processo.",
        },
        {
          title: "Superfícies Lisas Ideais para CIP",
          text: "O acabamento passivado reduz a aderência de cristais, facilita a remoção da torta em secadores Nutsche e favorece limpezas CIP/SIP completas.",
        },
        {
          title: "Resistência a Solventes e Reagentes",
          text: "O aço inoxidável AISI 316L assegura compatibilidade química contra solventes orgânicos agressivos e soluções ácidas ou alcalinas de sanitização.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Produtos Relacionados",
      intro: "Conheça soluções integradas de filtração sanitária e meios metálicos de alta precisão desenvolvidos pela MutualFilter.",
      cards: [
        {
          title: "Filtração de Alimentos e Farmacêutica",
          text: "Soluções completas de separação metálica para processos sanitários, clarificação de bebidas e purificação farmacêutica.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Equipamento sanitário de filtração em aço inoxidável polido para alimentos e farmacêutica",
          },
        },
        {
          title: "Filtração no Processamento de Alimentos",
          text: "Telas tecidas e peneiras de arame em cunha para derivados de leite, óleos comestíveis, xaropes e bebidas.",
          to: "/FoodProcessingFiltrationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Processamento de Alimentos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
            alt: "Elementos filtrantes em tela de aço inoxidável em unidade de processamento de alimentos",
          },
        },
        {
          title: "Malha Sinterizada",
          text: "Laminados sinterizados multicamadas e de 5 camadas unidos por difusão para placas filtrantes Nutsche e leitos fluidizados.",
          to: "/SinteredWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Malha Sinterizada",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado sinterizado multicamadas para secadores de filtro Nutsche farmacêuticos",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cartuchos filtrantes totalmente soldados, cilindros e cestos sanitários para carcaças de alta pureza.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Cartucho filtrante sanitário metálico para vapor e líquidos farmacêuticos",
          },
        },
      ],
    },
    cta: {
      id: "pharma-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite uma Cotação para Telas e Filtros Farmacêuticos",
      lead: "Informe a retenção micrométrica, dimensões do elemento, tipo de conexão à carcaça e liga metálica para avaliação técnica e comercial.",
      secondaryLabel: "Voltar para Alimentos e Farmacêutica",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
